import { JSONPath } from 'jsonpath-plus';
import { showConfirmBox, showMessageBox } from './msgbox.jsx';
import { moveChildren, removeChildren } from './html.js';
import xhr from './xhr.js';

export function initXhrActions() {
    const targets = Array.from(document.querySelectorAll('a[data-url], button[data-url]'));
    for(const target of targets) {
        target.onclick = async () => {
            if(target.disabled)
                return;

            const url = target.dataset.url;
            if(typeof url !== 'string' || url.length < 1)
                return;

            const disableWithTarget = typeof target.dataset.disableWithTarget === 'string'
                ? (target.querySelector(target.dataset.disableWithTarget) ?? target)
                : target;
            const originalBody = document.createDocumentFragment();
            moveChildren(disableWithTarget, originalBody);

            try {
                target.disabled = true;
                if(target.dataset.disableWith)
                    disableWithTarget.insertAdjacentHTML('beforeend', target.dataset.disableWith);

                if(target.dataset.confirm && !await showConfirmBox(target.dataset.confirm, 'Are you sure?'))
                    return;

                const headers = {};
                let content = null;

                if(target.dataset.body) {
                    headers['Content-Type'] = target.dataset.bodyType ?? 'application/octet-stream';
                    content = target.dataset.body;
                }

                const { status, body } = await xhr.send(
                    target.dataset.method ?? 'GET',
                    url,
                    {
                        type: 'json',
                        authed: target.dataset.withAuth,
                        csrf: target.dataset.withCsrf,
                        headers,
                    },
                    content,
                );

                if(status >= 400)
                    await showMessageBox(
                        body?.error?.text ?? `No additional information was provided. (HTTP ${status})`,
                        'Failed to complete action'
                    );
                else if(status >= 200 && status <= 299) {
                    if(target.dataset.refreshOnSuccess)
                        location.reload();
                    else {
                        const redirectUrl = target.dataset.redirectOnSuccess;
                        if(typeof redirectUrl === 'string' && redirectUrl.startsWith('/') && !redirectUrl.startsWith('//'))
                            location.assign(redirectUrl);
                    }
                }
            } catch(ex) {
                console.error(ex);
                await showMessageBox(ex, 'Failed to complete action');
            } finally {
                removeChildren(disableWithTarget);
                disableWithTarget.appendChild(originalBody);
                target.disabled = false;
            }
        };
    }
};

export function initXhrForms() {
    const targets = Array.from(document.querySelectorAll('form[onsubmit="return false"]'));
    for(const target of targets) {
        target.removeAttribute('onsubmit');

        const confirms = Array.from(target.querySelectorAll('[data-confirms]'));
        for(const field of confirms) {
            const mirror = target.querySelector(field.dataset.confirms);
            if(!mirror)
                continue;

            const updateField = updateValue => {
                if(updateValue && field.value !== mirror.value)
                    field.value = '';
                field.pattern = mirror.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                field.classList.toggle('input__text--disabled', mirror.value === '');
            };

            updateField(true);
            mirror.addEventListener('input', () => { updateField(false); });
            mirror.addEventListener('blur', () => { updateField(true); });
        }

        target.onsubmit = async ev => {
            ev.preventDefault();

            if(ev.target.dataset.disabled)
                return;
            ev.target.dataset.disabled = '1';

            try {
                for(let i = 0; i < ev.target.elements.length; ++i) {
                    const element = ev.target.elements[i];
                    element.dataset.disabled = element.disabled ? '1' : '';
                    element.disabled = true;
                }

                let params = null;
                let query = !ev.target.hasAttribute('enctype');
                if(query || ev.target.enctype === 'application/x-www-form-urlencoded')
                    params = new URLSearchParams;
                else if(ev.target.enctype === 'multipart/form-data')
                    params = new FormData;
                if(params === null)
                    throw new Error('unsure what to do with the encoding here');

                const checkable = new Set;

                const read = field => {
                    if(!field.name)
                        return;

                    if(field instanceof HTMLInputElement && ['checkbox', 'radio'].includes(field.type)) {
                        checkable.add(field.name);
                        if(!field.checked)
                            return;
                    }

                    params.append(field.name, field.value);
                };

                if(ev.submitter)
                    read(ev.submitter);

                for(let i = 0; i < ev.target.elements.length; ++i) {
                    const element = ev.target.elements[i];
                    if(element instanceof HTMLTextAreaElement
                        || element instanceof HTMLSelectElement
                        || (element instanceof HTMLInputElement && !['button', 'image', 'reset', 'submit'].includes(element.type)))
                        read(element);
                }

                for(const name of checkable)
                    if(!params.has(name))
                        params.append(name, '');

                const method = ev.target.getAttribute('method') ?? ev.target.method;
                let action = ev.target.action;
                if(query) action += `?${params}`;

                const { status, body } = await xhr.send(method, action, {
                    type: 'json', authed: true, csrf: true,
                }, query ? null : params);

                if(status >= 400)
                    await showMessageBox(
                        body?.error?.text ?? `No additional information was provided. (HTTP ${status})`,
                        'Form submission failed'
                    );
                else if(status >= 200 && status <= 299) {
                    for(const field of Array.from(target.querySelectorAll('[required]')))
                        field.value = '';

                    for(const field of Array.from(target.querySelectorAll('[data-body-fields]')))
                        for(const param of field.dataset.bodyFields.split(';').map(val => val.trim()).filter(val => val !== '').map(val => val.split('=').map(val => val.trim())))
                            if(typeof param[0] === 'string')
                                field[param[1] ?? 'value'] = JSONPath({ path: param[0], json: body, wrap: false });

                    if(target.dataset.notice) {
                        const notice = JSONPath({ path: target.dataset.notice, json: body, wrap: false });
                        if(notice)
                            await showMessageBox(notice, 'Form submission');
                    }

                    if(target.dataset.redirect) {
                        const redirect = JSONPath({ path: target.dataset.redirect, json: body, wrap: false });
                        if(redirect)
                            location.assign(redirect);
                    }

                    if(target.dataset.refreshOnSuccess)
                        location.reload();
                }
            } catch(ex) {
                console.error(ex);
                await showMessageBox(ex, 'Form submission failed');
            } finally {
                for(let i = 0; i < ev.target.elements.length; ++i) {
                    const element = ev.target.elements[i];
                    element.disabled = element.dataset.disabled !== '';
                    delete element.dataset.disabled;
                }

                delete ev.target.dataset.disabled;
            }
        };
    }
}
